import React, { useState, useRef, useCallback } from 'react';
import {
  LucideIcon,
  MousePointer2,
  Route,
  Eraser,
  Undo2,
  Redo2,
  Minus,
  Plus,
  Maximize2,
  Eye,
  Thermometer,
  Zap,
  Boxes,
  AlertTriangle,
  Grid,
  LayoutGrid,
  Search,
  Image,
  FileCode,
  Save,
  CheckCircle2,
  Box,
  ArrowLeft,
  Columns3,
} from 'lucide-react';
import { useEditorStore, ToolType, LensType, SaveStatus } from '../../store/editorStore';
import { SetupWizard } from './SetupWizard';
import { RowGeneratorModal } from './RowGeneratorModal';
import { RackAllocationModal } from './RackAllocationModal';
import { PathwayGeneratorModal } from './PathwayGeneratorModal';
import { useShallow } from 'zustand/react/shallow';
import {
  normalizeRackStatus,
  normalizeRackRole,
  RACK_STATUS_METAS,
  RACK_ROLE_METAS,
} from '../../utils/rackLifecycle.logic';

interface EditorToolbarProps {
  dcName: string;
  dcCode: string;
  draftName?: string;
  onExportJson: () => void;
  onExportPng: () => void;
  onSave: () => void;
  onPublish: () => void;
  canPublish: boolean;
  isSaving?: boolean;
  stageRef: React.RefObject<any>;
}

const BTN_BASE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '4px 8px',
  borderRadius: 5,
  border: '1px solid transparent',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease',
  background: 'transparent',
  color: 'var(--text-secondary, #9ca3af)',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  lineHeight: 1,
};

const BTN_ACTIVE: React.CSSProperties = {
  ...BTN_BASE,
  background: 'var(--color-accent-blue-bg, rgba(59, 130, 246, 0.12))',
  border: '1px solid var(--color-accent-blue-border, rgba(59, 130, 246, 0.35))',
  color: 'var(--color-accent-blue, #60a5fa)',
};

interface ToolBtn {
  tool: ToolType;
  icon: LucideIcon;
  label: string;
  key: string;
}

const TOOLS: ToolBtn[] = [
  { tool: 'select',    icon: MousePointer2, label: 'Выбор',  key: 'V' },
  { tool: 'draw_tray', icon: Route,         label: 'Трасса', key: 'D' },
  { tool: 'erase',     icon: Eraser,        label: 'Ластик', key: 'E' },
];

const LENSES: { value: LensType; label: string; icon: LucideIcon }[] = [
  { value: 'none',        label: 'Обычный вид',  icon: Eye },
  { value: 'temperature', label: 'Температура',  icon: Thermometer },
  { value: 'power',       label: 'Мощность',     icon: Zap },
  { value: 'space',       label: 'Ёмкость (RU)', icon: Boxes },
  { value: 'spof',        label: 'Точки отказа', icon: AlertTriangle },
];

const SAVE_STATUS_LABEL: Record<SaveStatus, { text: string; color: string }> = {
  idle:   { text: 'Сохранено',    color: 'var(--text-muted, #6b7280)' },
  dirty:  { text: 'Не сохранено', color: 'var(--status-warning, #f59e0b)' },
  saving: { text: 'Сохранение…',  color: 'var(--color-accent-blue, #60a5fa)' },
  saved:  { text: 'Сохранено',    color: 'var(--status-normal, #10b981)' },
  error:  { text: 'Ошибка',       color: 'var(--status-critical, #ef4444)' },
};

export const EditorToolbar: React.FC<EditorToolbarProps> = ({
  dcName, dcCode, draftName,
  onExportJson, onExportPng, onSave, onPublish, canPublish, isSaving,
  stageRef,
}) => {
  const {
    tool, setTool, activeLens, setActiveLens,
    racks, setSelectedIds, setStageTransform,
    stageW, stageH, stageScale,
    undo, redo,
    saveStatus, showElevation, setShowElevation,
    trayDrawType, setTrayDrawType,
    showAisles, setShowAisles,
    showTrays, setShowTrays,
    showLabels, setShowLabels,
    showRowGenerator, setShowRowGenerator,
    showSetupWizard, setShowSetupWizard,
    showPathwayGenerator, setShowPathwayGenerator,
    setShowRackAllocation,
  } = useEditorStore(useShallow(state => ({
    tool: state.tool, setTool: state.setTool, activeLens: state.activeLens, setActiveLens: state.setActiveLens,
    racks: state.racks, setSelectedIds: state.setSelectedIds, setStageTransform: state.setStageTransform,
    stageW: state.stageW, stageH: state.stageH, stageScale: state.stageScale,
    undo: state.undo, redo: state.redo,
    saveStatus: state.saveStatus, showElevation: state.showElevation, setShowElevation: state.setShowElevation,
    trayDrawType: state.trayDrawType, setTrayDrawType: state.setTrayDrawType,
    showAisles: state.showAisles, setShowAisles: state.setShowAisles,
    showTrays: state.showTrays, setShowTrays: state.setShowTrays,
    showLabels: state.showLabels, setShowLabels: state.setShowLabels,
    showRowGenerator: state.showRowGenerator, setShowRowGenerator: state.setShowRowGenerator,
    showSetupWizard: state.showSetupWizard, setShowSetupWizard: state.setShowSetupWizard,
    showPathwayGenerator: state.showPathwayGenerator, setShowPathwayGenerator: state.setShowPathwayGenerator,
    setShowRackAllocation: state.setShowRackAllocation,
  })));

  const canUndo = useEditorStore(s => s._historyIdx > 0);
  const canRedo = useEditorStore(s => s._historyIdx < s._history.length - 1);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const searchRef = useRef<HTMLInputElement>(null);

  const handleSearch = useCallback((query: string) => {
    setSearch(query);
    if (!query.trim()) { setSearchResults([]); return; }
    const currentRacks = useEditorStore.getState().racks;
    const q = query.toLowerCase();
    const hits = Object.values(currentRacks).filter(r => {
      const status = normalizeRackStatus(r.status);
      const role = normalizeRackRole(r.role, r.rackType);
      const statusRu = (RACK_STATUS_METAS[status]?.labelRu || '').toLowerCase();
      const roleRu = (RACK_ROLE_METAS[role]?.labelRu || '').toLowerCase();
      return (
        (r.label || '').toLowerCase().includes(q) ||
        (r.rackType || '').toLowerCase().includes(q) ||
        status.includes(q) ||
        statusRu.includes(q) ||
        role.toLowerCase().includes(q) ||
        roleRu.includes(q)
      );
    }).slice(0, 8);
    setSearchResults(hits);
  }, []);

  const flyTo = useCallback((rack: any) => {
    const rw = (rack.widthM || 0.6) * 100;
    const rh = (rack.depthM || 1.0) * 100;
    const targetScale = 1.5;
    const tx = stageW / 2 - (rack.x + rw / 2) * targetScale;
    const ty = stageH / 2 - (rack.y + rh / 2) * targetScale;
    setStageTransform(tx, ty, targetScale);
    setSelectedIds([rack.id]);
    setSearch('');
    setSearchResults([]);
  }, [stageW, stageH, setStageTransform, setSelectedIds]);

  // Zoom controls
  const zoomBy = useCallback((factor: number) => {
    const stage = stageRef.current;
    if (!stage) return;
    const oldScale = stage.scaleX();
    const newScale = Math.max(0.08, Math.min(8, oldScale * factor));
    const cx = stageW / 2;
    const cy = stageH / 2;
    const mousePointTo = {
      x: (cx - stage.x()) / oldScale,
      y: (cy - stage.y()) / oldScale,
    };
    setStageTransform(
      cx - mousePointTo.x * newScale,
      cy - mousePointTo.y * newScale,
      newScale,
    );
  }, [stageRef, stageW, stageH, setStageTransform]);

  const zoomFit = useCallback(() => {
    const allRacks = Object.values(racks);
    if (!allRacks.length) {
      setStageTransform(40, 40, 1);
      return;
    }
    const minX = Math.min(...allRacks.map(r => r.x));
    const minY = Math.min(...allRacks.map(r => r.y));
    const maxX = Math.max(...allRacks.map(r => r.x + (r.widthM || 0.6) * 100));
    const maxY = Math.max(...allRacks.map(r => r.y + (r.depthM || 1.0) * 100));
    const pad = 60;
    const scaleX = (stageW - pad * 2) / (maxX - minX || 1);
    const scaleY = (stageH - pad * 2) / (maxY - minY || 1);
    const scale = Math.max(0.08, Math.min(4, Math.min(scaleX, scaleY)));
    const tx = (stageW - (maxX - minX) * scale) / 2 - minX * scale;
    const ty = (stageH - (maxY - minY) * scale) / 2 - minY * scale;
    setStageTransform(tx, ty, scale);
  }, [racks, stageW, stageH, setStageTransform]);

  const saveInfo = SAVE_STATUS_LABEL[saveStatus];

  const divider = (
    <div style={{ width: 1, height: 18, background: 'var(--border, #1a2744)', margin: '0 2px', flexShrink: 0 }} />
  );

  return (
    <>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '0 8px',
        background: 'var(--bg-panel, #0d1117)',
        borderBottom: '1px solid var(--border, #1a2744)',
        height: 44,
        flexShrink: 0,
        position: 'relative',
        zIndex: 20,
        overflowX: 'auto',
      }}
      className="scrollbar-none"
    >
      {/* Back button */}
      <a
        href={`/${dcCode}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 8px',
          borderRadius: 5,
          color: 'var(--text-secondary, #9ca3af)',
          textDecoration: 'none',
          background: 'var(--bg-hover, rgba(255,255,255,0.04))',
          border: '1px solid var(--border, rgba(255,255,255,0.06))',
          fontSize: 11,
          fontWeight: 500,
          marginRight: 4,
          transition: 'color 0.12s ease, background 0.12s ease',
        }}
        title="Вернуться к дашборду ЦОД"
      >
        <ArrowLeft size={13} />
        <span>Панель</span>
      </a>

      {/* Breadcrumbs */}
      <div style={{ display: 'flex', alignItems: 'center', marginRight: 4, whiteSpace: 'nowrap', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-accent-blue, #60a5fa)', letterSpacing: '0.02em' }}>
          {dcName || dcCode}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-muted, #4b5563)' }}>/</span>
        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary, #f3f4f6)' }}>
          План зала
        </span>
        {draftName && (
          <span style={{ fontSize: 11, color: 'var(--text-secondary, #9ca3af)', display: 'inline-flex', alignItems: 'center', gap: 3, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={draftName}>
            <span style={{ color: 'var(--text-muted, #4b5563)' }}>›</span>
            {draftName}
          </span>
        )}
      </div>

      {divider}

      {/* Tools segmented group */}
      <div style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--bg-deep, #060a0f)', padding: '2px', borderRadius: 6, border: '1px solid var(--border, #1a2744)', gap: 1 }}>
        {TOOLS.map(t => {
          const Icon = t.icon;
          const isActive = tool === t.tool;
          return (
            <button
              key={t.tool}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 6px',
                borderRadius: 4,
                border: 'none',
                background: isActive ? 'var(--color-accent-blue-bg, rgba(59, 130, 246, 0.15))' : 'transparent',
                color: isActive ? 'var(--color-accent-blue, #60a5fa)' : 'var(--text-secondary, #9ca3af)',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: isActive ? 600 : 500,
                transition: 'all 0.12s ease',
              }}
              title={`${t.label} (${t.key})`}
              onClick={() => setTool(t.tool)}
            >
              <Icon size={13} />
              <kbd style={{ fontSize: 9, opacity: 0.6, background: 'var(--bg-input, #1f2937)', padding: '1px 3px', borderRadius: 2, fontFamily: 'var(--font-data)' }}>
                {t.key}
              </kbd>
            </button>
          );
        })}
      </div>

      {/* Tray draw type options */}
      {tool === 'draw_tray' && (
        <div style={{ display: 'inline-flex', gap: 2, background: 'var(--bg-deep, #060a0f)', padding: 2, borderRadius: 6, border: '1px solid var(--border, #1a2744)' }}>
          <button
            onClick={() => setTrayDrawType('fiber')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: trayDrawType === 'fiber' ? 'rgba(245, 158, 11, 0.15)' : 'transparent',
              color: trayDrawType === 'fiber' ? '#fbbf24' : 'var(--text-muted, #6b7280)',
              border: trayDrawType === 'fiber' ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid transparent',
              padding: '3px 7px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 500
            }}
            title="Оптический лоток (Fiber Raceway)"
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fbbf24' }} />
            Оптика
          </button>
          <button
            onClick={() => setTrayDrawType('copper')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: trayDrawType === 'copper' ? 'rgba(14, 165, 233, 0.15)' : 'transparent',
              color: trayDrawType === 'copper' ? '#38bdf8' : 'var(--text-muted, #6b7280)',
              border: trayDrawType === 'copper' ? '1px solid rgba(14, 165, 233, 0.4)' : '1px solid transparent',
              padding: '3px 7px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 500
            }}
            title="Проволочный лоток СКС (Wire Mesh)"
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#38bdf8' }} />
            Медь
          </button>
          <button
            onClick={() => setTrayDrawType('power')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: trayDrawType === 'power' ? 'rgba(239, 68, 68, 0.15)' : 'transparent',
              color: trayDrawType === 'power' ? '#f87171' : 'var(--text-muted, #6b7280)',
              border: trayDrawType === 'power' ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid transparent',
              padding: '3px 7px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 500
            }}
            title="Шинный мост / Силовой лоток (Power Busway)"
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444' }} />
            Сила
          </button>
        </div>
      )}

      {divider}

      {/* Undo/Redo */}
      <div style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--bg-deep, #060a0f)', padding: '2px', borderRadius: 6, border: '1px solid var(--border, #1a2744)', gap: 1 }}>
        <button
          style={{ ...BTN_BASE, padding: '4px 6px', opacity: canUndo ? 1 : 0.35 }}
          title="Отменить (Ctrl+Z)"
          disabled={!canUndo}
          onClick={undo}
        >
          <Undo2 size={13} />
        </button>
        <button
          style={{ ...BTN_BASE, padding: '4px 6px', opacity: canRedo ? 1 : 0.35 }}
          title="Повторить (Ctrl+Y)"
          disabled={!canRedo}
          onClick={redo}
        >
          <Redo2 size={13} />
        </button>
      </div>

      {divider}

      {/* Zoom controls */}
      <div style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--bg-deep, #060a0f)', padding: '2px', borderRadius: 6, border: '1px solid var(--border, #1a2744)', gap: 2 }}>
        <button style={{ ...BTN_BASE, padding: '4px 6px' }} title="Уменьшить (Ctrl+-)" onClick={() => zoomBy(1 / 1.25)}>
          <Minus size={12} />
        </button>
        <span style={{ fontSize: 10, fontFamily: 'var(--font-data)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary, #9ca3af)', minWidth: 32, textAlign: 'center' }}>
          {Math.round(stageScale * 100)}%
        </span>
        <button style={{ ...BTN_BASE, padding: '4px 6px' }} title="Увеличить (Ctrl++)" onClick={() => zoomBy(1.25)}>
          <Plus size={12} />
        </button>
        <button
          style={{
            ...BTN_BASE,
            padding: '3px 6px',
            fontSize: 10,
            borderLeft: '1px solid var(--border, #1a2744)',
            borderRadius: '0 4px 4px 0',
          }}
          title="Уместить всё в экран (Ctrl+Shift+F)"
          onClick={zoomFit}
        >
          <Maximize2 size={11} style={{ marginRight: 3 }} />
          Fit
        </button>
      </div>

      {divider}

      {/* Lens selector */}
      <div style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
        <select
          value={activeLens}
          onChange={e => setActiveLens(e.target.value as LensType)}
          style={{
            background: 'var(--bg-deep, #060a0f)',
            border: '1px solid var(--border, #1a2744)',
            borderRadius: 6,
            color: activeLens !== 'none' ? 'var(--color-accent-blue, #60a5fa)' : 'var(--text-secondary, #9ca3af)',
            fontSize: 11,
            fontWeight: 500,
            padding: '4px 8px',
            cursor: 'pointer',
            outline: 'none',
          }}
          title="Аналитические тепловые и емкостные слои (Линзы)"
        >
          {LENSES.map(l => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
      </div>

      {divider}

      {/* Layers toggles */}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        <div style={{ display: 'inline-flex', gap: 1, background: 'var(--bg-deep, #060a0f)', padding: '2px', borderRadius: 6, border: '1px solid var(--border, #1a2744)' }}>
          <button
            id="toggle-aisles-btn"
            onClick={() => setShowAisles(!showAisles)}
            style={{
              background: showAisles ? 'var(--color-accent-blue-bg, rgba(59, 130, 246, 0.15))' : 'transparent',
              border: 'none',
              padding: '3px 6px',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 11,
              color: showAisles ? 'var(--color-accent-blue, #60a5fa)' : 'var(--text-muted, #6b7280)',
              fontWeight: showAisles ? 600 : 400,
              transition: 'all 0.12s ease',
            }}
            title="Показать/скрыть зоны коридоров"
          >
            Зоны
          </button>
          <button
            id="toggle-trays-btn"
            onClick={() => setShowTrays(!showTrays)}
            style={{
              background: showTrays ? 'var(--color-accent-blue-bg, rgba(59, 130, 246, 0.15))' : 'transparent',
              border: 'none',
              padding: '3px 6px',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 11,
              color: showTrays ? 'var(--color-accent-blue, #60a5fa)' : 'var(--text-muted, #6b7280)',
              fontWeight: showTrays ? 600 : 400,
              transition: 'all 0.12s ease',
            }}
            title="Показать/скрыть кабельные трассы"
          >
            Трассы
          </button>
          <button
            id="toggle-labels-btn"
            onClick={() => setShowLabels(!showLabels)}
            style={{
              background: showLabels ? 'var(--color-accent-blue-bg, rgba(59, 130, 246, 0.15))' : 'transparent',
              border: 'none',
              padding: '3px 6px',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 11,
              color: showLabels ? 'var(--color-accent-blue, #60a5fa)' : 'var(--text-muted, #6b7280)',
              fontWeight: showLabels ? 600 : 400,
              transition: 'all 0.12s ease',
            }}
            title="Показать/скрыть метки стоек"
          >
            Метки
          </button>
        </div>
      </div>

      {divider}

      {/* Search quick jumper */}
      <div style={{ position: 'relative' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          background: 'var(--bg-deep, #060a0f)',
          border: '1px solid var(--border, #1a2744)',
          borderRadius: 6,
          padding: '2px 6px',
          width: 120,
          transition: 'width 0.15s ease, border-color 0.15s ease',
        }}>
          <Search size={11} style={{ color: 'var(--text-muted, #6b7280)', flexShrink: 0 }} />
          <input
            ref={searchRef}
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Поиск..."
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-primary, #f3f4f6)',
              fontSize: 11,
              outline: 'none',
              width: '100%',
              padding: '2px 0',
            }}
            onFocus={e => {
              e.target.parentElement!.style.borderColor = 'var(--color-accent-blue, #3b82f6)';
              e.target.parentElement!.style.width = '170px';
              if (search.trim()) {
                handleSearch(search);
              }
            }}
            onBlur={e => {
              e.target.parentElement!.style.borderColor = 'var(--border, #1a2744)';
              e.target.parentElement!.style.width = '120px';
              setTimeout(() => setSearchResults([]), 350);
            }}
          />
        </div>
        {searchResults.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: 220,
            marginTop: 4,
            background: 'var(--bg-panel, #111827)',
            border: '1px solid var(--border, #1a2744)',
            borderRadius: 6,
            overflow: 'hidden',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.6), 0 8px 10px -6px rgba(0, 0, 0, 0.4)',
            zIndex: 999,
          }}>
            {searchResults.map(rack => (
              <div
                key={rack.id}
                onMouseDown={() => flyTo(rack)}
                style={{
                  padding: '7px 12px',
                  fontSize: 11,
                  color: 'var(--text-primary, #e2e8f0)',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--border-color-subtle, #1f2937)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--color-accent-blue, #60a5fa)', fontWeight: 600 }}>{rack.label}</span>
                  {rack.status && rack.status !== 'active' && (
                    <span style={{
                      fontSize: 8,
                      padding: '1px 4px',
                      borderRadius: 2,
                      background: rack.status === 'deprecated' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(56, 189, 248, 0.2)',
                      color: rack.status === 'deprecated' ? '#f59e0b' : '#38bdf8',
                      fontFamily: 'JetBrains Mono, monospace',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                    }}>
                      {rack.status}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'var(--text-muted, #6b7280)' }}>
                  {rack.role && (
                    <span style={{ color: '#94a3b8', fontFamily: 'JetBrains Mono, monospace' }}>
                      [{rack.role}]
                    </span>
                  )}
                  <span>{rack.rackType}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Automation & CAD Generators */}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <button
          style={{
            ...BTN_BASE,
            background: 'var(--color-accent-blue-bg, rgba(59,130,246,0.1))',
            border: '1px solid var(--color-accent-blue-border, rgba(59,130,246,0.3))',
            color: 'var(--color-accent-blue, #60a5fa)',
            padding: '3px 7px',
          }}
          title="Генератор рядов и коридоров (R)"
          onClick={() => setShowRowGenerator(true)}
        >
          <Grid size={12} />
          <span>Ряд</span>
          <kbd style={{ fontSize: 9, opacity: 0.6, background: 'var(--bg-input, #1f2937)', padding: '1px 3px', borderRadius: 2, fontFamily: 'var(--font-data)' }}>R</kbd>
        </button>
        <button
          style={{
            ...BTN_BASE,
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            color: '#fbbf24',
            padding: '3px 7px',
          }}
          title="Матричный мастер автогенерации зала (W)"
          onClick={() => setShowSetupWizard(true)}
        >
          <LayoutGrid size={12} />
          <span>Матрица</span>
          <kbd style={{ fontSize: 9, opacity: 0.6, background: 'var(--bg-input, #1f2937)', padding: '1px 3px', borderRadius: 2, fontFamily: 'var(--font-data)' }}>W</kbd>
        </button>
        <button
          style={{
            ...BTN_BASE,
            background: 'rgba(168, 85, 247, 0.12)',
            border: '1px solid rgba(168, 85, 247, 0.35)',
            color: '#c084fc',
            padding: '3px 7px',
          }}
          title="Пакетное распределение стоек по группам и владельцам (M)"
          onClick={() => setShowRackAllocation(true)}
        >
          <Columns3 size={12} />
          <span>Группы</span>
          <kbd style={{ fontSize: 9, opacity: 0.6, background: 'var(--bg-input, #1f2937)', padding: '1px 3px', borderRadius: 2, fontFamily: 'var(--font-data)' }}>M</kbd>
        </button>
        <button
          id="open-pathway-generator-btn"
          style={{
            ...BTN_BASE,
            background: showPathwayGenerator ? 'rgba(14, 165, 233, 0.25)' : 'rgba(14, 165, 233, 0.12)',
            border: `1px solid ${showPathwayGenerator ? '#38bdf8' : 'rgba(14, 165, 233, 0.35)'}`,
            color: '#38bdf8',
            padding: '3px 7px',
          }}
          title="Автоматическая генерация кабельных лотков и магистралей (P)"
          onClick={() => setShowPathwayGenerator(true)}
        >
          <Route size={12} />
          <span>Магистрали</span>
          <kbd style={{ fontSize: 9, opacity: 0.6, background: 'var(--bg-input, #1f2937)', padding: '1px 3px', borderRadius: 2, fontFamily: 'var(--font-data)' }}>P</kbd>
        </button>
        <button
          style={showElevation ? BTN_ACTIVE : BTN_BASE}
          title="Вид сбоку на стойки и кабель-росты (Z-ось)"
          onClick={() => setShowElevation(!showElevation)}
        >
          <Box size={12} />
          <span>Z-вид</span>
        </button>
      </div>

      <div style={{ flex: 1, minWidth: 8 }} />

      {/* Save status chip */}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 7px', borderRadius: 4, background: 'var(--bg-deep, #060a0f)', border: '1px solid var(--border, #1a2744)', flexShrink: 0 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: saveInfo.color }} />
        <span style={{ fontSize: 10, fontWeight: 500, color: saveInfo.color, whiteSpace: 'nowrap' }}>
          {saveInfo.text}
        </span>
      </div>

      {/* Export actions */}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        <button
          style={{ ...BTN_BASE, padding: '4px 6px' }}
          title="Экспорт плана в изображение PNG"
          onClick={onExportPng}
        >
          <Image size={13} />
        </button>
        <button
          style={{ ...BTN_BASE, padding: '4px 6px' }}
          title="Экспорт топологии в JSON"
          onClick={onExportJson}
        >
          <FileCode size={13} />
        </button>
      </div>

      {/* Save / Publish */}
      <button
        style={{
          ...BTN_BASE,
          background: saveStatus === 'dirty' ? 'var(--color-accent-blue-bg)' : 'transparent',
          border: saveStatus === 'dirty' ? '1px solid var(--color-accent-blue-border)' : '1px solid var(--border, rgba(255,255,255,0.08))',
          color: saveStatus === 'dirty' ? 'var(--color-accent-blue)' : 'var(--text-secondary)',
          fontWeight: 600,
          padding: '4px 8px',
          flexShrink: 0,
        }}
        title="Сохранить черновик на сервере (Ctrl+S)"
        onClick={onSave}
        disabled={isSaving}
      >
        <Save size={13} />
        <span>Сохранить</span>
      </button>

      {canPublish && (
        <button
          style={{
            ...BTN_BASE,
            background: 'rgba(16, 185, 129, 0.15)',
            border: '1px solid rgba(16, 185, 129, 0.4)',
            color: 'var(--status-normal, #10b981)',
            fontWeight: 600,
            padding: '4px 8px',
            flexShrink: 0,
          }}
          title="Опубликовать схему и применить в боевую модель ЦОД"
          onClick={onPublish}
        >
          <CheckCircle2 size={13} />
          <span>Опубликовать</span>
        </button>
      )}
    </div>
    {showSetupWizard && <SetupWizard onClose={() => setShowSetupWizard(false)} />}
    {showRowGenerator && <RowGeneratorModal onClose={() => setShowRowGenerator(false)} />}
    {showPathwayGenerator && <PathwayGeneratorModal onClose={() => setShowPathwayGenerator(false)} />}
    <RackAllocationModal />
  </>);
};
